/**
 * ADR-70 cut 6e (hitlist 9.2): the max bids standing on the lot in the ring,
 * as the ring console shows them.
 *
 * That maxes stand is always the floor's: the auctioneer must know a proxy is
 * competing. Whether the console may look closer is the tenant's capability,
 * which only a Super Admin switches on; while it is off the line says so in
 * the hitlist's words (Active proxy bid: YES, amount and bidder hidden). With
 * it on, Show asks the ring (VIEW_MAX_BIDS); the ring records the look and
 * answers this screen alone with what the viewer's role may see (Q13: an
 * admin everything, a clerk whether a max stands and whose, never the amount).
 *
 * Pure, so the words are testable without a browser or a ring.
 */

import type { MaxBidsFrame } from '@hipnumber/protocol-client';
import type { BidderReg } from './ring-history.js';
import type { LotWords } from './lot-term.js';

export interface MaxBidLine {
  /** The words under "Bids this lot". */
  text: string;
  /** Offer Show: a Super Admin turned visibility on and a max stands. */
  canView: boolean;
}

/**
 * The max-bid line from the floor snapshot; null with no lot up, or on a ring
 * older than cut 6e (its snapshot carries no `activeMaxBids`).
 */
export function maxBidLine(snap: Readonly<Record<string, unknown>> | null): MaxBidLine | null {
  if (!snap || typeof snap['lotId'] !== 'string' || !('activeMaxBids' in snap)) return null;
  const n = snap['activeMaxBids'];
  if (typeof n !== 'number' || !Number.isSafeInteger(n) || n < 0) return null;
  if (n === 0) return { text: 'No max bids', canView: false };
  if (snap['maxBidVisibility'] !== true) return { text: `Active proxy bid: YES${n > 1 ? ` (${n})` : ''} · Amount: hidden · Bidder: hidden`, canView: false };
  return { text: `${n} max ${n === 1 ? 'bid' : 'bids'} standing`, canView: true };
}

export interface MaxBidsRow {
  who: string;
  amount: string;
  left: string;
}

export interface MaxBidsView {
  /** Who looked, and what that role is shown. */
  role: string;
  /** The columns this role may see; a hidden one reads "Hidden" on every row. */
  columns: { bidder: boolean; amount: boolean; left: boolean };
  /** In the order the maxes were set. */
  rows: MaxBidsRow[];
  /** Said when the ring found none (one can go between the line and the look). */
  empty: string | null;
}

const money = (n: number): string => '$' + n.toLocaleString('en-US');

/** A ring bidder in the clerk's words: paddle and name from the registration. */
export function maxBidderWords(bidderId: string, regs: readonly BidderReg[]): string {
  const r = regs.find((g) => !!g.ringBidderId && g.ringBidderId === bidderId);
  if (!r) return `Bidder ${bidderId.slice(0, 8)} (not on this sale's list)`;
  const pad = r.onlinePaddle ?? r.livePaddle;
  const words = [pad !== null ? `Paddle ${pad}` : null, r.bidderName ?? null].filter((w): w is string => !!w);
  return words.length ? words.join(' · ') : `Bidder ${bidderId.slice(0, 8)}`;
}

/** What the max-bids sheet shows of one MAX_BIDS answer. */
export function maxBidsView(f: MaxBidsFrame, regs: readonly BidderReg[]): MaxBidsView {
  const columns = {
    bidder: f.scopes.includes('view_proxy_bidder'),
    amount: f.scopes.includes('view_proxy_amount'),
    left: f.scopes.includes('view_proxy_remaining_capacity'),
  };
  const role = f.viewerRole === 'admin'
    ? 'Admin view: bidder, max, and what is left above the high bid.'
    : columns.bidder ? 'Clerk view: whose max it is. The amount stays hidden.' : 'Clerk view: that a max stands. The amount and the bidder stay hidden.';
  const rows = f.viewed.map((v, i): MaxBidsRow => ({
    who: columns.bidder && v.bidderId ? maxBidderWords(v.bidderId, regs) : `Max ${i + 1}`,
    amount: columns.amount && typeof v.maxAmount === 'number' ? money(v.maxAmount) : 'Hidden',
    // null: no bid on the lot yet, so nothing to measure the room against.
    left: !columns.left ? 'Hidden' : typeof v.remaining === 'number' ? money(v.remaining) : 'No bid yet',
  }));
  return { role, columns, rows, empty: f.viewed.length === 0 ? 'No max bids stand on this lot now.' : null };
}

/** The activity-feed words for a look at the maxes (MAX_BID_VIEWED on the floor wire, figures dropped). */
export function maxBidViewedFeed(ev: Readonly<Record<string, unknown>>, hip: string, words: LotWords): string {
  const n = typeof ev['activeCount'] === 'number' ? ev['activeCount'] : 0;
  const who = ev['viewerRole'] === 'admin' ? 'an admin' : 'the clerk';
  return `max bids on ${words.low} ${hip} looked at by ${who} (${n} standing)`;
}
